import { classify } from 'sidecar-utilities'

//modalPaddingX is used by header, body, and footer
const modalPaddingX = 'px-5 sm:px-6 lg:px-8'

export const overlayStyles = classify({
  color: 'color-overlay',
  position: 'fixed inset-0 z-[700]',
  animation: 'data-[state=open]:animate-overlay-show',
})

//controls styles for the dialog
export const modalStyles = ({
  size,
  mobileFullHeight,
}: {
  size?: 'lg' | 'md' | 'sm'
  mobileFullHeight?: boolean
}) =>
  classify({
    styles:
      'color-bgDefaultContainer elevation-2nd-floor rounded-t-xl xs:rounded-xl focus:outline-none',
    width: 'w-full',
    // Content height by default. `mobileFullHeight` fills the full available
    // height on mobile only (`max-xs`, where the modal is a bottom drawer) so a
    // modal whose content changes height doesn't jump the drawer around; from
    // `xs` up (centered dialog) it stays content-height.
    height: mobileFullHeight
      ? 'h-fit max-xs:h-[calc(100%-var(--safe-top)-theme(spacing.4))]'
      : 'h-fit',
    maxWidth: size === 'lg' ? 'max-w-3xl' : 'max-w-120',
    maxHeight:
      'max-h-[calc(100%-var(--safe-top)-theme(spacing.4))] xs:max-h-[calc(100%-var(--safe-top)-theme(spacing.8))]',
    flex: 'flex flex-col',
    position:
      'fixed bottom-0 xs:top-1/2 xs:left-1/2 xs:-translate-x-1/2 xs:-translate-y-1/2',
    // Same z-index as the overlay (see `overlayStyles`), NOT higher. When a
    // modal is opened on top of another, both dialogs sit in the same stacking
    // context (portaled to <body>); with equal z-indexes the later dialog wins
    // on DOM order, so the top modal's overlay paints above — and dims — the
    // modal beneath. A higher value here made the underlying modal's content
    // out-rank the top modal's overlay, so it showed through at full brightness.
    zIndex: 'z-[700]',
    animation: 'data-[state=open]:modal-show',
  })

// Row that lays a thumbnail beside the title block. Carries no padding of its
// own so it can also be used by a consumer rendering the heading inside the
// modal body (where the body already supplies the horizontal padding).
export const headerRowStyles = 'flex items-center gap-3'

export const headerStyles = classify({
  modalPaddingX,
  paddingY: 'py-4 sm:py-6',
  headerRowStyles,
})

// Keeps the title and subtitle stacked vertically together inside the row.
export const headingStackStyles = 'w-full flex flex-col gap-2'

export const titleStyles = 'text-xl md:text-2xl lining-nums m-0'

export const subtitleStyles = 'sh-body m-0'

export const closeButtonStyles = 'p-1 -mr-1 self-start'

export const bodyStyles = ({
  hasFooter,
  hasHeader = true,
  scrolled,
  mobileFullHeight,
}: {
  hasFooter?: boolean
  hasHeader?: boolean
  scrolled?: boolean
  mobileFullHeight?: boolean
}) =>
  classify({
    modalPaddingX,
    scrollarea: 'self-stretch max-h-screen overflow-y-auto',
    // On a mobile full-height modal (`max-xs`) the body fills the space between
    // header and footer so the footer pins to the bottom; `min-h-0` lets this
    // flex child shrink and scroll its own overflow. No effect from `xs` up.
    fill: mobileFullHeight ? 'max-xs:grow max-xs:min-h-0' : '',
    // With no header the body owns the top padding the header used to provide,
    // matching it so the content sits where a heading would have.
    topPadding: hasHeader ? '' : 'pt-4 sm:pt-6',
    // Separates the body from the header once content scrolls under it. With no
    // header there is nothing to separate it from, and the border would just draw
    // a stray line across the modal's own top edge.
    scroll: scrolled && hasHeader ? 'border-t color-border-weak-default' : '',
    bottomPadding:
      !hasFooter || scrolled
        ? 'pb-[calc(var(--safe-bottom)+theme(space.5))] md:pb-6'
        : '',
    bottomScrollBorder: hasFooter && scrolled ? 'border-b' : '',
  })

export const footerStyles = classify({
  modalPaddingX,
  topPadding: 'pt-4 sm:pt-6',
  bottomPadding:
    'pb-[calc(var(--safe-bottom)+theme(space.4))] sm:pb-[calc(var(--safe-bottom)+theme(space.6))]',
  flex: 'flex justify-between items-center',
  // mb-0 removes the default margin bottom from buttons.
  // ml-auto is for when there is only one child in the footer.
  // ml-auto will make the child stick to the right side.
  removeBtnMargin: '[&_button]:mb-0 [&_button:last-child]:ml-auto',
})
